/** @jsxImportSource @emotion/react */
import React from "react";
import { Alert, Col, Form, Input, Modal, Row, Button } from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useHistory } from "react-router-dom";
import { RegexFormat } from "../../../common/regexFormat";
import * as Styles from "./styles";
import { AuthUsecase } from "../../../usecases/Auth";
import { Constant } from "../../../common/constant";
import { useAuthDispatch } from "../../../usecases/Auth";

interface IFormInputs {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const formSchema = yup.object().shape({
  currentPassword: yup
    .string()
    .min(8, "Your password must be 8 characters or more.")
    .matches(
      RegexFormat.Password,
      "Password must contain at least one: lower case, upper case, number and symbol character.",
    )
    .required("Please enter current password"),
  newPassword: yup
    .string()
    .min(8, "Your password must be 8 characters or more.")
    .matches(
      RegexFormat.Password,
      "Password must contain at least one: lower case, upper case, number and symbol character.",
    )
    .required("Please enter new password"),
  confirmNewPassword: yup
    .string()
    .oneOf(
      [yup.ref("newPassword"), null],
      "Please make sure confirm password match",
    ),
});

interface ModalChangePasswordProps {
  onCancel: () => void;
  visible: boolean;
}

const ModalChangePasswordComponent: React.FC<ModalChangePasswordProps> = ({
  visible,
  onCancel,
}: ModalChangePasswordProps) => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<IFormInputs>({
    mode: "onChange",
    resolver: yupResolver(formSchema),
  });
  const history = useHistory();
  const authDispatch = useAuthDispatch();

  const handleSubmitHandle = React.useCallback(
    async (data: IFormInputs) => {
      setIsProcessing(true);
      console.error(data);
      try {
        const authUsecase = new AuthUsecase(authDispatch);
        const cognitoUser = await authUsecase.currentAuthenticatedUser();
        await authUsecase.changePassword({
          user: cognitoUser,
          oldPassword: data.currentPassword,
          newPassword: data.newPassword,
        });
        reset();
        setErrorMessage("");
        history.push(Constant.route.homeBuilder.login);
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
      } finally {
        setIsProcessing(false);
      }
    },
    [authDispatch, history, reset],
  );

  return (
    <Modal
      title="Change Password"
      visible={visible}
      closable={false}
      footer={[
        <Button key="back" size="large" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="large"
          loading={isProcessing}
          onClick={handleSubmit(handleSubmitHandle)}
        >
          Save
        </Button>,
      ]}
    >
      <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
        {errorMessage && (
          <Row justify="center">
            <Col css={Styles.colGutterStyle}>
              <Alert message={errorMessage} type="error" />
            </Col>
          </Row>
        )}
        <Form.Item
          label="Current password"
          css={Styles.labelStyle}
          validateStatus={errors.currentPassword ? "error" : "validating"}
          help={errors.currentPassword?.message}
        >
          <Controller
            name="currentPassword"
            control={control}
            render={({ field }) => <Input.Password {...field} size="large" />}
          />
        </Form.Item>
        <Form.Item
          label="New password"
          css={Styles.labelStyle}
          validateStatus={errors.newPassword ? "error" : "validating"}
          help={errors.newPassword?.message}
        >
          <Controller
            name="newPassword"
            control={control}
            render={({ field }) => <Input.Password {...field} size="large" />}
          />
        </Form.Item>
        <Form.Item
          label="Confirm new password"
          css={Styles.labelStyle}
          validateStatus={errors.confirmNewPassword ? "error" : "validating"}
          help={errors.confirmNewPassword?.message}
        >
          <Controller
            name="confirmNewPassword"
            control={control}
            render={({ field }) => <Input.Password {...field} size="large" />}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const ModalChangePassword = React.memo(ModalChangePasswordComponent);
